import { BookmarkTree } from "@/features/bookmark";
import { useWorkspaceDataflowId } from "@/features/workspace/context/WorkspaceContext";
import { useBookmarksContext } from "@/stores/bookmark";
import { useActiveDataflow, useDataflowStore } from "@/stores/dataflow";
import { useSidebar, useUIStore } from "@/stores/ui";
import { useUserFiles } from "@/stores/user-file";
import type {
  Bookmark,
  BookmarkSetBookmark,
} from "@/types/europafacts/Bookmark";
import { Flex } from "@mantine/core";
import "./WorkspaceSidebarDiscover.css";

export function WorkspaceSidebarDiscover() {
  const { bookmarks, bookmarkSets } = useBookmarksContext();
  const userFiles = useUserFiles();
  const dataflowData = useActiveDataflow();
  const workspaceDataflowId = useWorkspaceDataflowId();
  const dataflowId = useDataflowStore((state) => state.activeDataflowId);
  const addTransformer = useDataflowStore((state) => state.addTransformer);
  const updateTransformer = useDataflowStore(
    (state) => state.updateTransformer
  );
  const openSidebar = useUIStore((state) => state.openSidebar);
  const sidebar = useSidebar();

  function hasExistingInput(existingInputIndex: number | undefined): boolean {
    return existingInputIndex !== undefined && existingInputIndex !== -1;
  }

  const handleDatasetSelect = async (nodeValue: string) => {
    const existingInputIndex = dataflowData?.flow_definition_json?.findIndex(
      (item) => item.type === "data_input" || item.type === "user_file"
    );

    const userFile = userFiles.find((f) => f.id === nodeValue);
    if (userFile) {
      const newTransformer = {
        type: "user_file" as const,
        file_id: userFile.id,
        file_name: userFile.name,
        file_type: userFile.type,
      };

      if (hasExistingInput(existingInputIndex) && dataflowId) {
        updateTransformer(dataflowId, existingInputIndex!, newTransformer);
      } else if (dataflowId) {
        addTransformer(dataflowId, newTransformer);
      }

      if (sidebar.onAction) {
        sidebar.onAction();
      } else {
        openSidebar(workspaceDataflowId, "flow");
      }
      return;
    }

    let selectedDataset: Bookmark | BookmarkSetBookmark | null = null;

    const bookmark = bookmarks.find((b) => b.bookmark_id === nodeValue);
    if (bookmark) {
      selectedDataset = bookmark;
    } else {
      for (const bookmarkSet of bookmarkSets || []) {
        const bookmarkSetBookmark =
          bookmarkSet.bookmark_set_bookmark_list?.find(
            (b: any) => b.bookmark_set_bookmark_id === nodeValue
          );
        if (bookmarkSetBookmark) {
          selectedDataset = bookmarkSetBookmark;
          break;
        }
      }
    }

    if (selectedDataset) {
      const dbTableId = selectedDataset.db_table.db_table_id;
      const newTransformer = {
        type: "data_input" as const,
        source: "public",
        db_table_id: dbTableId,
      };

      if (hasExistingInput(existingInputIndex) && dataflowId) {
        updateTransformer(dataflowId, existingInputIndex!, newTransformer);
      } else if (dataflowId) {
        addTransformer(dataflowId, newTransformer);
      }

      if (sidebar.onAction) {
        sidebar.onAction();
      }
    }
  };

  return (
    <Flex p="md" style={{ width: "100%", minWidth: 0 }}>
      <BookmarkTree onDatasetSelect={handleDatasetSelect} />
    </Flex>
  );
}
